import { normalize } from 'normalizr';
import { ActionTree } from 'vuex';
import { ClassroomModel } from '@descomplica/dex-models';
import {
  classroom as classroomSchema,
  appointment,
} from '@descomplica/dex-store/lib/normalizr_schema';
import { dexQuery } from '@/helpers/graphql';
import { ClassroomState } from './state';
import RootState from '@/store/root_state';
import {
  appointmentsByClassroomIds,
  classroomAppointmentsByClassroomId,
  classroomBoardSectionsByClassroomId,
  classroomInfoQuery,
  classroomMaterialsByClassroomId,
  classroomReadingsByClassroomId,
  getClassroomsProgress,
  revisionListsByClassroomSlug,
} from '../../queries/dexQueries';
import { UndergradGradesService } from '@/services/UndergradGrades';

const actions: ActionTree<ClassroomState, RootState> = {
  async PULL_CLASSROOM_INFO(
    { commit, dispatch },
    {
      userId,
      classroomSlug,
      courseCategory,
    }: { userId: number; classroomSlug: string; courseCategory: string },
  ) {
    commit('SET_LOADING_CLASSROOM_DATA', { isLoading: true });

    const response = await dexQuery(classroomInfoQuery, { userId, classroomSlug });
    const firstClassroomReturned = response.data.allClassrooms.nodes[0];

    if (!firstClassroomReturned) {
      commit('SET_LOADING_CLASSROOM_DATA', { isLoading: false });
      return false;
    }

    const normalizedResponse = normalize(firstClassroomReturned, classroomSchema);

    commit('SET_CLASSROOM_STUDENT_IDS', normalizedResponse.entities);
    commit('SET_BOARDS', normalizedResponse.entities);
    commit('SET_BOARDS_CLASSROOMS', normalizedResponse.entities);
    commit('SET_BOARDS_TEACHERS', normalizedResponse.entities);
    commit('SET_TEACHERS', normalizedResponse.entities);
    commit('SET_BOARD_SECTIONS', normalizedResponse.entities);
    commit('SET_CLASSROOMS', normalizedResponse.entities);

    dispatch('PULL_CLASSROOM_MATERIALS', { classroomId: firstClassroomReturned.id });
    if (courseCategory !== 'pos') {
      dispatch('PULL_REVISION_LISTS', {
        classroomId: firstClassroomReturned.id,
        classroomSlug: firstClassroomReturned.slug,
        userId,
      });
    }
    dispatch('PULL_CLASSROOM_BOARDS_SECTIONS', { classroomId: firstClassroomReturned.id, userId });
    dispatch('PULL_CLASSROOM_READINGS', { classroomId: firstClassroomReturned.id });
    dispatch('PULL_CLASSROOM_APPOINTMENTS', { classroomId: firstClassroomReturned.id });
    dispatch('PULL_CLASSROOMS_PROGRESS', { classroomIds: [firstClassroomReturned.id] });

    commit('SET_LOADING_CLASSROOM_DATA', { isLoading: false });

    return true;
  },

  async PULL_CLASSROOM_MATERIALS({ commit, dispatch }, { classroomId }: { classroomId: number }) {
    commit('SET_LOADING_CLASSROOM_MATERIALS', { isLoading: true, classroomId });

    const response = await dexQuery(classroomMaterialsByClassroomId, { classroomId });

    if (!response) {
      commit('SET_LOADING_CLASSROOM_MATERIALS', { isLoading: false, classroomId });
      return;
    }

    const classroomMaterials = response.data.classroomById;
    const normalizedResponse = normalize(classroomMaterials, classroomSchema);

    const { lists } = normalizedResponse.entities;
    if (lists) {
      const listIds = Object.keys(lists).map((listId) => Number(listId));
      dispatch('PULL_LISTS_PROGRESS', { listIds });
    }

    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_BOARDS', normalizedResponse.entities);
    commit('SET_MATERIALS', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_LOADING_CLASSROOM_MATERIALS', { isLoading: false, classroomId });
  },

  async PULL_CLASSROOM_INFO_FOR_POSTGRAD_STUDYPLANNING(
    { commit, dispatch },
    { classroomId }: { classroomId: number },
  ) {
    const response = await dexQuery(classroomMaterialsByClassroomId, { classroomId });
    if (!response) return;
    const classroomMaterials = response.data.classroomById;
    const normalizedResponse = normalize(classroomMaterials, classroomSchema);

    const { lists } = normalizedResponse.entities;
    if (lists) {
      const listIds = Object.keys(lists).map((listId) => Number(listId));
      await dispatch('PULL_LISTS_PROGRESS', { listIds });
    }

    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
  },

  async PULL_CLASSROOM_READINGS({ commit }, { classroomId }: { classroomId: number }) {
    const response = await dexQuery(classroomReadingsByClassroomId, { classroomId });

    if (!response) return;

    const classroomReadings = response.data.classroomById;

    const normalizedResponse = normalize(classroomReadings, classroomSchema);

    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_BOARDS', normalizedResponse.entities);
    commit('SET_BOARDS_CLASSROOMS', normalizedResponse.entities);
    commit('SET_READINGS', normalizedResponse.entities);
  },

  async PULL_CLASSROOM_APPOINTMENTS({ commit }, { classroomId }: { classroomId: number }) {
    commit('SET_LOADING_CLASSROOM_APPOINTMENTS', { isLoading: true, classroomId });

    const response = await dexQuery(classroomAppointmentsByClassroomId, { classroomId });

    if (!response) {
      commit('SET_LOADING_CLASSROOM_APPOINTMENTS', { isLoading: false, classroomId });
      return;
    }

    const classroomAppointments = response.data.classroomById;

    const normalizedResponse = normalize(classroomAppointments, classroomSchema);

    commit('SET_APPOINTMENTS', normalizedResponse.entities);
    commit('SET_LIVES', normalizedResponse.entities);
    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_LOADING_CLASSROOM_APPOINTMENTS', { isLoading: false, classroomId });
  },

  async PULL_CLASSROOM_BOARDS_SECTIONS(
    { commit },
    { classroomId, userId }: { classroomId: number; userId: number },
  ) {
    const response = await dexQuery(classroomBoardSectionsByClassroomId, { classroomId, userId });

    if (!response) return;

    const classroomMaterials = response.data.classroomById;
    const normalizedResponse = normalize(classroomMaterials, classroomSchema);

    if (normalizedResponse.entities.assertions) {
      const assertions = await new UndergradGradesService().getAssertionsCorrections(
        Object.keys(normalizedResponse.entities.assertions).map((key: any) => Number(key)),
      );

      Object.entries(normalizedResponse.entities.assertions).forEach(([key, value]) => {
        const relatedAssertion = assertions.find(({ id }: any) => id === Number(key));

        if (relatedAssertion) value.correct = relatedAssertion.correct;
      });
    }

    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_BOARD_SECTIONS', normalizedResponse.entities);
    commit('SET_BOARDS', normalizedResponse.entities);
    commit('SET_BOARDS_CLASSROOMS', normalizedResponse.entities);
  },

  async PULL_CLASSROOMS_APPOINTMENTS({ commit }, payload) {
    const { classrooms } = payload;
    const classroomIds = classrooms.map((classroom: ClassroomModel) => classroom.id);

    if (!classroomIds || classroomIds.length === 0) return;

    const startDate = new Date();
    const response = await dexQuery(appointmentsByClassroomIds, { classroomIds, startDate });

    const normalizedResponse = normalize(response.data.allAppointments, {
      nodes: [appointment],
    });

    commit('SET_APPOINTMENTS', normalizedResponse.entities);
    commit('SET_LIVES', normalizedResponse.entities);
  },

  async PULL_REVISION_LISTS({ commit }, payload) {
    const { classroomSlug, userId, classroomId } = payload;

    commit('SET_LOADING_REVISION_LIST', { isLoading: true, classroomId });

    const response = await dexQuery(revisionListsByClassroomSlug, { classroomSlug, userId });

    const normalizedResponse = normalize(response.data.classroomBySlug, classroomSchema);

    if (normalizedResponse.entities.assertions) {
      const assertions = await new UndergradGradesService().getAssertionsCorrections(
        Object.keys(normalizedResponse.entities.assertions).map((key: any) => Number(key)),
      );

      Object.entries(normalizedResponse.entities.assertions).forEach(([key, value]) => {
        const relatedAssertion = assertions.find(({ id }: any) => id === Number(key));

        if (relatedAssertion) value.correct = relatedAssertion.correct;
      });
    }

    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_LIST_ITEMS', normalizedResponse.entities);
    commit('SET_MATERIALS', normalizedResponse.entities);
    commit('SET_QUESTIONS', normalizedResponse.entities);
    commit('SET_ASSERTIONS', normalizedResponse.entities);
    commit('SET_REPLIES', normalizedResponse.entities);
    commit('SET_LOADING_REVISION_LIST', { isLoading: false, classroomId });
  },

  async PULL_CLASSROOMS_PROGRESS({ commit }, { classroomIds }: { classroomIds: number[] }) {
    if (!classroomIds.length) return;

    const response = await dexQuery(getClassroomsProgress, { classroomIds });
    const classroomsProgresses = response?.data?.getClassroomsProgress;
    if (!classroomsProgresses) return;

    commit('SET_CLASSROOMS_PROGRESS', classroomsProgresses);
  },
};

export default actions;
